import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { resetPassword } from "@/api/auth";
import { ApiError } from "@/api/client";

// Stessa pagina per due link diversi: il reset di chi ha dimenticato la password
// e il primo accesso di chi arriva dal benvenuto. Cambiano solo i testi.
const TESTI = {
  reset: {
    titolo: "Reimposta password",
    sotto: "Scegli una nuova password per il tuo account.",
    bottone: "Reimposta password",
    attesa: "Aggiornamento...",
    fattoTitolo: "Password aggiornata",
    fattoTesto: "La tua password è stata reimpostata. Puoi effettuare il login con la nuova password.",
    scaduto: "Il link è scaduto. Richiedi un nuovo reset.",
    senzaToken: "Il link di reset non contiene un token valido.",
    erroreRete: "Errore nel reset della password. Riprova.",
  },
  "primo-accesso": {
    titolo: "Scegli la tua password",
    sotto: "Ancora un passaggio e sei dentro.",
    bottone: "Salva e entra",
    attesa: "Salvataggio...",
    fattoTitolo: "Password salvata",
    fattoTesto: "Il tuo accesso è pronto. Entra con il tuo nome utente e la password che hai appena scelto.",
    scaduto: "Il link è scaduto. Chiedi un nuovo link a chi ti ha creato l'accesso.",
    senzaToken: "Il link non contiene un indirizzo valido. Chiedi un nuovo link a chi ti ha creato l'accesso.",
    erroreRete: "Errore nel salvataggio della password. Riprova.",
  },
} as const;

export function ResetPasswordPage({ modo = "reset" }: { modo?: keyof typeof TESTI }) {
  const t = TESTI[modo];
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="bg-card rounded-xl border border-border p-8 text-center">
        <h2 className="text-xl font-semibold text-text mb-2">Link non valido</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t.senzaToken}
        </p>
        <button
          onClick={() => navigate("/login", { replace: true })}
          className="text-sm text-brand underline"
        >
          Torna al login
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-card rounded-xl border border-border p-8 text-center">
        <div className="inline-flex bg-brand/10 rounded-2xl p-3 mb-4">
          <CheckCircle2 size={26} className="text-brand" />
        </div>
        <h2 className="text-xl font-semibold text-text mb-2">{t.fattoTitolo}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t.fattoTesto}
        </p>
        <button
          onClick={() => navigate("/login", { replace: true })}
          className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
        >
          Vai al login
        </button>
      </div>
    );
  }

  const isValid = newPassword.length >= 8 && newPassword === confirmPassword;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || !token) return;
    setLoading(true);
    setErrorMsg("");

    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.detail === "token_expired") {
          setErrorMsg(t.scaduto);
        } else if (err.detail === "token_invalid") {
          setErrorMsg("Il link non è valido o è già stato usato.");
        } else if (err.detail === "password_too_short") {
          setErrorMsg("La password deve essere di almeno 8 caratteri.");
        } else {
          setErrorMsg(`Errore (${err.status}). Riprova.`);
        }
      } else {
        setErrorMsg(t.erroreRete);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-card rounded-xl border border-border p-8">
      <h2 className="text-xl font-semibold text-text mb-2">{t.titolo}</h2>
      <p className="text-sm text-muted-foreground mb-4">{t.sotto}</p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-text mb-1">Nuova password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
            disabled={loading}
            className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-brand disabled:opacity-50"
          />
          {newPassword.length > 0 && newPassword.length < 8 && (
            <p className="text-xs text-red-500 mt-1">Minimo 8 caratteri</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-text mb-1">Conferma password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-brand disabled:opacity-50"
          />
          {confirmPassword.length > 0 && newPassword !== confirmPassword && (
            <p className="text-xs text-red-500 mt-1">Le password non coincidono</p>
          )}
        </div>
        <button
          type="submit"
          disabled={!isValid || loading}
          className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
        >
          {loading ? t.attesa : t.bottone}
        </button>
        {errorMsg && (
          <p className="text-xs text-red-600 text-center">{errorMsg}</p>
        )}
      </form>
    </div>
  );
}
